import React, { useRef, useState, useContext, useEffect, useMemo } from "react";
import { useMasters } from "../../contexts/MastersContext";
import {
  X,
  User,
  Mail,
  Shield,
  Clock,
  Calendar,
  UserCheck,
  Building,
  MoreVertical,
  UploadCloud,
} from "lucide-react";
import { useUsers } from "../../contexts/UsersContext";
import Modal from "../../components/UI/Modal";
import UserForm from "../../components/Form/UserForm";
import ReusableTable from "../../components/UI/ReusableTable";
import Notification from "../../components/UI/Notification";
import DeleteButton from "../../components/UI/AlertDialog";
import Drawer from "../../components/UI/Drawer";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx"; // Add this import for reading Excel files
import DropdownMenu from "../../components/UI/DropdownMenu";
import { FaSearch, FaPlus, FaUpload, FaTimes } from "react-icons/fa";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(
  ({
    row,
    openMenu,
    setOpenMenu,
    openModalForEdit,
    handleDelete,
    openDrawerForDetails,
    setOpenDropdownId,
  }) => {
    const buttonRef = useRef(null);

    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() =>
            setOpenMenu(openMenu === row.user_id ? null : row.user_id)
          }
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-8 h-8 text-gray-600 dark:text-gray-400 hover:bg-gray-200 p-2 rounded-full transition duration-150" />
        </button>

        {openMenu === row.user_id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={() => {
                openDrawerForDetails(row);
                setOpenDropdownId(null);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
            >
              View Details
            </button>
            <button
              onClick={(e) => {
                openModalForEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>
            <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={(e) => {
                handleDelete(row.user_id || row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  },
);

const Users = () => {
  const {
    users,
    fetchUsers,
    addUser,
    updateUser,
    deleteUser,
    pagination,
    loading,
  } = useUsers();
  const { roles } = useMasters();
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [importFile, setImportFile] = useState(null); // Add state for file selection
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]); // For previewing file data
  const [isDragActive, setIsDragActive] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const tabs = [
    { id: "basic", label: "Basic Info", icon: <User size={16} /> },
    { id: "access", label: "Access & Roles", icon: <Shield size={16} /> },
    { id: "activity", label: "Activity", icon: <Clock size={16} /> },
  ];

  // open modal for add user
  const openModalForAdd = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  // open modal for edit user
  const openModalForEdit = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  // close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  // open drawer for user details
  const openDrawerForDetails = (employee) => {
    setSelectedUser(employee);
    setIsDrawerOpen(true);
  };

  // To add or update user
  const handleFormSubmit = async (user) => {
    if (editingUser) {
      const result = await updateUser({
        id: editingUser.user_id || editingUser.id,
        ...user,
      });
      if (result?.success === true) {
        setNotification({
          message: "User updated successfully.",
          type: "success",
        });
        closeModal();
      } else {
        setNotification({
          message: `Error: ${result?.message || "Failed to update user."}`,
          type: "error",
        });
      }
      return result;
    } else {
      const result = await addUser(user);
      if (result?.success === true) {
        setNotification({
          message: "User created successfully.",
          type: "success",
        });
        closeModal();
      } else {
        setNotification({
          message: `Error: ${result?.message || "Failed to add user."}`,
          type: "error",
        });
      }
      return result;
    }
  };

  // To delete user
  const handleDelete = async (id) => {
    const result = await deleteUser(id);
    // console.log("result from handle delete: ", result)
    if (result?.success === true) {
      // setNotification("User deleted successfully.");
      setNotification({
        message: "User deleted successfully.",
        type: "success",
      });
    } else {
      // setNotification(`Error: ${result?.message || "Failed to delete user."}`);
      setNotification({
        message: `Error: ${result?.message || "Failed to delete user."}`,
        type: "error",
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest(".dropdown-container")) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  // using this to get the index to each page
  const getColumns = (currentPage, pageSize) => [
    {
      label: "Sr. No.",
      field: "sr_no",
      render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
    },
    {
      label: "EMP ID",
      field: "employee_id",
      render: (row) => (
        <span className="block max-w-[200px] truncate" title={row.employee_id}>
          {row.employee_id || "-"}
        </span>
      ),
    },
    {
      label: "Full Name",
      field: "full_name",
      render: (row) => (
        <span
          className="block max-w-[200px] truncate"
          title={row.full_name || row.name}
        >
          {row.full_name || row.name}
        </span>
      ),
    },
    {
      label: "Email",
      field: "email",
      render: (row) => (
        <span className="block max-w-[200px] truncate" title={row.email}>
          {row.email}
        </span>
      ),
    },
    {
      label: "Phone",
      field: "phone",
      render: (row) => row.phone || "-",
    },
    {
      label: "Role",
      field: "role",
      render: (row) => row.role || (row.roles && row.roles[0]?.name) || "-",
    },
    {
      label: "Manager",
      field: "manager_name",
      render: (row) => row.manager_name || row.manager?.name || "-",
    },
    {
      label: "Actions",
      field: "actions",
      render: (row) => (
        // <div className="flex items-center justify-center">
        //   <div className="relative dropdown-container">
        //     <button
        //       onClick={(e) => handleDropdownToggle(row.id, e)}
        //       className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        //     >
        //       <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        //     </button>

        //     {openDropdownId === row.id && (
        //       <div className={`absolute right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] min-w-[200px] ${
        //         dropdownPosition === 'top'
        //           ? 'bottom-full mb-1'
        //           : 'top-full mt-1'
        //       }`} style={{
        //         maxHeight: '300px',
        //         overflowY: 'auto'
        //       }}>
        //         <div className="py-1 flex flex-col space-y-0">
        //           {/* View Details */}
        //           <button
        //             onClick={() => {
        //               openDrawerForDetails(row);
        //               setOpenDropdownId(null);
        //             }}
        //             className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
        //           >
        //             View Details
        //           </button>

        //           {/* Edit */}
        //           <button
        //             onClick={() => {
        //               openModalForEdit(row);
        //               setOpenDropdownId(null);
        //             }}
        //             className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
        //           >
        //             Edit
        //           </button>

        //           {/* Delete - Only show if not current user */}
        //           {user.name !== row.name && (
        //             <DeleteButton
        //               style={`text-red-600 hover:text-red-800 px-3 py-2`}
        //               onDelete={() => handleDelete(row.id)}
        //             />
        //           )}
        //         </div>
        //       </div>
        //     )}
        //   </div>
        // </div>

        <ActionsColumn
          row={row}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          openModalForEdit={openModalForEdit}
          openDrawerForDetails={openDrawerForDetails}
          setOpenDropdownId={setOpenDropdownId}
          handleDelete={handleDelete}
        />
      ),
    },
  ];

  // page change handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchUsers(newPage);
    }
  };

  // exclude filter columns props for reusable table component
  const excludeFilterColumns = ["id", "actions", "updated_at", "created_at"];

  // Detail card component
  function DetailCard({ icon, label, value, children, highlight = false }) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 h-full">
        <div className="flex items-center mb-1">
          {icon}
          <p className="text-sm font-medium text-gray-600 dark:text-gray-500 ml-2">
            {label}
          </p>
        </div>
        <p
          className={`${highlight ? "font-semibold font-poppins" : "font-normal"} text-gray-800 dark:text-white`}
        >
          {value || "N/A"}
        </p>
        {children}
      </div>
    );
  }

  // Timeline item component
  function TimelineItem({ title, date, description, icon }) {
    const formatDate = (dateStr) => {
      return new Date(dateStr).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Asia/Kolkata",
      });
    };

    return (
      <div className="flex font-poppins">
        <div className="flex flex-col items-center mr-4">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            {icon}
          </div>
          <div className="h-full w-px bg-gray-200 mt-2"></div>
        </div>
        <div className="pb-5">
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-400">
              {title}
            </h4>
            <span className="ml-2 text-xs text-gray-500">
              {formatDate(date)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    );
  }

  const processSelectedFile = (file) => {
    const allowedExtensions = ["csv", "txt", "xlsx", "xls"];
    if (!file) return;
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setNotification({
        message: `Invalid file type. Please select a .csv, .txt, .xlsx, or .xls file.`,
        type: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      setPreviewData(jsonData);
    };
    reader.readAsArrayBuffer(file);

    setImportFile(file);
    setNotification({
      message: `File "${file.name}" selected for preview.`,
      type: "success",
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    processSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    processSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      setNotification({
        message: "Please select a file to import users.",
        type: "error",
      });
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", importFile);

      const token = localStorage.getItem("authToken");
      const response = await fetch(
        "https://hrmspayroll.prepstripe.com/api/users/import",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (response.ok) {
        setNotification({
          message: data?.message || "Users imported successfully.",
          type: "success",
        });
        setIsImportModalOpen(false);
        fetchUsers(); // Refresh the user list
      } else {
        setNotification({
          message: data?.message || "Failed to import users.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error importing users:", error);
      setNotification({
        message: "An error occurred while importing users.",
        type: "error",
      });
    } finally {
      setIsImportModalOpen(false);
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // Fetch file from public folder (relative URL)
      const response = await fetch("/UserHRMS.xlsx");
      const blob = await response.blob();

      // Create temp download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "HRMS_User_Import_sample.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const filteredUsers = (users || []).filter((u) => {
    const fullName = (
      u.full_name ||
      u.name ||
      `${u.first_name || ""} ${u.last_name || ""}`
    ).toLowerCase();
    const email = (u.email || "").toLowerCase();
    const employeeId = (u.employee_id || "").toLowerCase();

    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      email.includes(searchTerm.toLowerCase()) ||
      employeeId.includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (u.status || "Active").toLowerCase() === statusFilter.toLowerCase();

    const matchesRole =
      roleFilter === "all" ||
      (
        u.role ||
        u.role_name ||
        (u.roles && u.roles[0]?.name) ||
        ""
      ).toLowerCase() === roleFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <div className="">
      {/* notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="w-full mx-auto dark:bg-gray-950 font-poppins rounded p-4 md:p-6">
        {/* title and toolbar */}
        <div className="flex flex-col gap-6 mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-white font-poppins">
              Users
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 w-full">
            {/* Search Bar - Left aligned */}
            <div className="relative w-full lg:flex-1 lg:max-w-[500px]">
              <input
                type="text"
                placeholder="Search by name, email or employee ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
              />
              <FaSearch className="absolute left-3.5 top-3.5 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3.5 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-center sm:justify-start lg:justify-end">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer shadow-sm min-w-[130px]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer shadow-sm min-w-[150px]"
              >
                <option value="all">All Roles</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>

              <button
                onClick={openModalForAdd}
                className="bg-blue-600 text-sm dark:hover:bg-gray-900 transform transition duration-150 font-poppins dark:bg-gray-800 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 flex items-center gap-2 shadow-sm shrink-0"
              >
                <FaPlus className="w-4 h-4" /> Add User
              </button>
              <button
                onClick={() => setIsImportModalOpen(true)}
                className="bg-green-600 text-sm dark:hover:bg-gray-900 transform transition duration-150 font-poppins dark:bg-gray-800 text-white px-4 py-2.5 rounded-md hover:bg-green-700 flex items-center gap-2 shadow-sm shrink-0"
              >
                <FaUpload className="w-4 h-4" /> Import
              </button>
            </div>
          </div>
        </div>
        {/* title and toolbar */}

        {/* ReusableTable */}
        <ReusableTable
          // data={[...users].reverse()}
          data={filteredUsers}
          columns={getColumns(currentPage, pagination.perPage)}
          filterable={false}
          excludeFilterColumns={excludeFilterColumns}
          loading={loading}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
        />

        {/* Pagination */}
        {!isFiltering && users?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}

        {/* Import Modal */}
        <Modal
          isOpen={isImportModalOpen}
          onRequestClose={() => {
            setIsImportModalOpen(false);
            setImportFile(null);
            setPreviewData([]);
          }}
          title="Import Users"
        >
          <div className="space-y-4">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`relative flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg p-6 transition ${
                isDragActive
                  ? "border-blue-500 bg-blue-50/50 dark:border-blue-400 dark:bg-blue-900/20"
                  : "border-gray-300 dark:border-gray-600"
              } bg-gray-50 dark:bg-gray-800`}
            >
              <UploadCloud className="w-8 h-8 text-gray-500 dark:text-gray-300 mb-2" />
              <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                Drag and drop your file here, or
                <label
                  htmlFor="user-import-file"
                  className="text-blue-600 hover:underline ml-1 cursor-pointer"
                >
                  browse
                </label>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Supported: .csv, .txt, .xlsx, .xls
              </p>
              <input
                id="user-import-file"
                type="file"
                accept=".csv, .txt, .xlsx, .xls"
                onChange={handleFileChange}
                disabled={isLoading}
                className="hidden"
              />
              {importFile && (
                <div className="mt-3 text-xs text-gray-700 dark:text-gray-200">
                  Selected:{" "}
                  <span className="font-medium">{importFile.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setImportFile(null);
                      setPreviewData([]);
                    }}
                    className="ml-2 text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            {previewData.length > 0 && (
              <div className="overflow-x-auto overflow-y-auto max-h-96 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md p-4">
                <h3 className="text-lg font-semibold mb-2 dark:text-white">
                  Preview Data
                </h3>
                <table className="min-w-full table-auto text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      {Object.keys(previewData[0]).map((key) => (
                        <th
                          key={key}
                          className="px-4 py-2 text-left text-black dark:text-gray-300 font-medium"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index} className="border-b dark:border-gray-600">
                        {Object.values(row).map((value, idx) => (
                          <td
                            key={idx}
                            className="px-4 py-2 text-gray-800 dark:text-gray-200"
                          >
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-sm text-gray-500 mt-2">
                  Showing all rows of data.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={handleDownload}
                className="inline-flex items-center bg-green-600 hover:bg-green-600 font-medium text-white px-4 py-2 rounded-md"
              >
                Download Sample File
              </button>
              <button
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setPreviewData([]);
                }}
                disabled={isLoading}
                className="px-4 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 font-medium dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleImportUsers}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </Modal>

        {/* Modal for Adding/Editing Users */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          title={editingUser ? "Edit User" : "Add User"}
        >
          <UserForm
            closeModal={closeModal}
            onSubmit={handleFormSubmit}
            initialValues={editingUser}
          />
        </Modal>

        {/* Drawer for User Details */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          title="Employee Details"
          ChilDiv=""
          ChildParent=""
          height_width="md:max-w-3xl md:h-max-h-3xl"
          border_radius="md:rounded-md"
          showClose={false}
        >
          {selectedUser ? (
            <div>
              {/* Header */}
              <div className="bg-blue-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                      <User size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium">
                        {selectedUser?.full_name || selectedUser?.name}
                      </h3>
                      <p className="text-indigo-100 text-sm">
                        {selectedUser?.role ||
                          (selectedUser?.roles && selectedUser?.roles[0]?.name)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="p-1 rounded-full hover:bg-blue-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b dark:border-gray-700 border-gray-200 dark:bg-black">
                <div className="flex px-4 overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-3 px-4 text-sm border-b-2 ${
                        activeTab === tab.id
                          ? "border-blue-500 text-blue-600 dark:text-white font-medium"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="p-5 dark:bg-black h-fit">
                {activeTab === "basic" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <DetailCard
                        icon={<UserCheck size={18} className="text-blue-500" />}
                        label="Employee ID"
                        value={selectedUser?.employee_id || "-"}
                        highlight={true}
                      />
                      <DetailCard
                        icon={<User size={18} className="text-blue-500" />}
                        label="Full Name"
                        value={
                          selectedUser?.full_name || selectedUser?.name || "-"
                        }
                        highlight={true}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DetailCard
                        icon={<Mail size={18} className="text-blue-500" />}
                        label="Email"
                        value={selectedUser?.email || "-"}
                      />
                      <DetailCard
                        icon={<Clock size={18} className="text-blue-500" />}
                        label="Phone"
                        value={selectedUser?.phone || "-"}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DetailCard
                        icon={<Calendar size={18} className="text-blue-500" />}
                        label="Date of Birth"
                        value={selectedUser?.date_of_birth || "-"}
                      />
                      <DetailCard
                        icon={<Calendar size={18} className="text-blue-500" />}
                        label="Date of Joining"
                        value={selectedUser?.date_of_joining || "-"}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DetailCard
                        icon={<User size={18} className="text-blue-500" />}
                        label="Gender"
                        value={selectedUser?.gender || "-"}
                      />
                      <DetailCard
                        icon={<Building size={18} className="text-blue-500" />}
                        label="Department"
                        value={
                          selectedUser?.department_name ||
                          selectedUser?.department?.name ||
                          selectedUser?.department ||
                          "-"
                        }
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DetailCard
                        icon={<Shield size={18} className="text-blue-500" />}
                        label="Designation"
                        value={
                          selectedUser?.designation_name ||
                          selectedUser?.designation?.name ||
                          selectedUser?.designation ||
                          "-"
                        }
                      />
                      <DetailCard
                        icon={<UserCheck size={18} className="text-blue-500" />}
                        label="Employee Type"
                        value={
                          selectedUser?.employment_type ||
                          selectedUser?.employment_type?.name ||
                          "-"
                        }
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DetailCard
                        icon={<Building size={18} className="text-blue-500" />}
                        label="Manager"
                        value={
                          selectedUser?.manager_name ||
                          selectedUser?.manager?.name ||
                          "-"
                        }
                      />
                      <DetailCard
                        icon={<UserCheck size={18} className="text-blue-500" />}
                        label="Secondary Approver"
                        value={
                          selectedUser?.secondary_approver_name ||
                          selectedUser?.secondary_approver?.name ||
                          "-"
                        }
                      />
                    </div>
                  </div>
                )}

                {activeTab === "access" && (
                  <div className="space-y-5 h-full">
                    {/* Assigned Roles */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <Shield size={18} className="text-blue-500 mr-2" />
                        <h4 className="font-medium text-gray-700 dark:text-white">
                          Assigned Roles
                        </h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.roles?.map((role, index) => (
                          <span
                            key={index}
                            className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded"
                          >
                            {role.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "activity" && (
                  <div className="space-y-5">
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-white mb-3 flex items-center">
                        <Clock size={16} className="mr-2 text-blue-500" />
                        Account Timeline
                      </h4>
                      <div className="space-y-4">
                        <TimelineItem
                          title="Account Updated"
                          date={selectedUser?.updated_at}
                          description="User information was last modified"
                          icon={<Clock size={14} className="text-blue-500" />}
                        />
                        <TimelineItem
                          title="Account Created"
                          date={selectedUser?.created_at}
                          description="User was added to the system"
                          icon={
                            <Calendar size={14} className="text-green-500" />
                          }
                        />
                        {selectedUser.email_verified_at && (
                          <TimelineItem
                            title="Email Verified"
                            date={selectedUser?.email_verified_at}
                            description="User verified their email address"
                            icon={<Mail size={14} className="text-blue-500" />}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 px-4">No data to display</p>
          )}
        </Drawer>
      </div>
    </div>
  );
};

export default Users;
